import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export const UnsubscribeDialog = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState<{ title: string; description: string } | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const unsubscribed = params.get('unsubscribed');
    const unsubscribeError = params.get('unsubscribe_error');
    const subscriptionType = params.get('type');

    if (unsubscribed === 'true') {
      let typeLabel = 'notifications';
      
      switch (subscriptionType) {
        case 'new_comment':
          typeLabel = 'comment notifications for this post';
          break;
        case 'comment_reply':
          typeLabel = 'reply notifications';
          break;
        case 'new_post':
          typeLabel = 'new post notifications';
          break;
        case 'weekly_digest':
          typeLabel = 'weekly digest emails';
          break;
      }

      setMessage({
        title: 'Successfully Unsubscribed',
        description: `You've been unsubscribed from ${typeLabel}. You can manage your subscriptions in your account settings.`,
      });
      setOpen(true);
    } else if (unsubscribeError) {
      let errorMessage = 'An error occurred while unsubscribing. Please try again.';
      
      switch (unsubscribeError) {
        case 'invalid':
          errorMessage = 'Invalid unsubscribe link. Please check the link and try again.';
          break;
        case 'invalid_or_used':
          errorMessage = 'This unsubscribe link is invalid or has already been used.';
          break;
        case 'expired':
          errorMessage = 'This unsubscribe link has expired. Please contact support if you need help.';
          break;
        case 'failed':
          errorMessage = 'Failed to unsubscribe. Please try again later.';
          break;
      }

      setMessage({
        title: 'Unsubscribe Error',
        description: errorMessage,
      });
      setOpen(true);
    }
  }, [location.search]);

  const handleClose = () => {
    setOpen(false);
    // Clean up URL parameters
    const params = new URLSearchParams(location.search);
    params.delete('unsubscribed');
    params.delete('unsubscribe_error');
    params.delete('type');
    params.delete('postId');
    
    const newSearch = params.toString();
    navigate(newSearch ? `${location.pathname}?${newSearch}` : location.pathname, { replace: true });
  };

  // Auto-dismiss after 8 seconds
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        handleClose();
      }, 8000);
      return () => clearTimeout(timer);
    }
  }, [open]);

  if (!message) return null;

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{message.title}</AlertDialogTitle>
          <AlertDialogDescription>{message.description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction onClick={handleClose}>Got it</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
